/**
 * D5's seven readiness checks (plan R2). get_mmm_readiness computes the values and READINESS holds the thresholds;
 * check 6, the scope, is the client's. Each check carries data, not words: the card keeps its literal t('…') keys.
 * readinessOf maps the RPC's row once, for box 1's read and the dataset route's gate alike.
 * Pure. api/_lib imports this module: relative imports only, never '@/'.
 *
 * @module services/budget/readinessRules
 */
import type { Database } from '../../types/database.types';
import { READINESS, type OutcomeMetric } from './budgetContract';

export type CheckId = 'history' | 'overlap' | 'channels' | 'continuity' | 'outcome' | 'scope' | 'currency';

/** get_mmm_readiness's row as PostgREST returns it (migration 131). */
export type ReadinessDb = Database['public']['Functions']['get_mmm_readiness']['Returns'][number];

/** get_mmm_readiness's row, camelCased (migration 131). */
export interface ReadinessRow {
  hasConfig: boolean;
  /** The confirmed config's, or before a confirm (R3) every non-analytics account with a currency. */
  scopeAccountIds: readonly string[];
  /** The config's, or 'revenue' before a confirm. */
  outcomeMetric: string;
  /** Bangkok yesterday. */
  cutoff: string;
  /** Both null exactly when weeks is 0. */
  windowStart: string | null;
  windowEnd: string | null;
  weeks: number;
  overlapWeeks: number;
  channels: readonly string[];
  gapChannels: readonly string[];
  outcomeWeeks: number;
  currencies: readonly string[];
  unknownCurrencyAccounts: number;
  lastMetricDate: string | null;
  lastOutcomeDate: string | null;
}

export const readinessOf = (r: ReadinessDb): ReadinessRow => ({
  hasConfig: r.has_config, scopeAccountIds: r.scope_account_ids, outcomeMetric: r.outcome_metric, cutoff: r.cutoff,
  windowStart: r.window_start, windowEnd: r.window_end, weeks: r.weeks, overlapWeeks: r.overlap_weeks,
  channels: r.channels, gapChannels: r.gap_channels, outcomeWeeks: r.outcome_weeks, currencies: r.currencies,
  unknownCurrencyAccounts: r.unknown_currency_accounts, lastMetricDate: r.last_metric_date,
  lastOutcomeDate: r.last_outcome_date,
});

/** The active version's scope and metric. */
export interface ActiveSummary { scopeAccountIds: readonly string[]; outcomeMetric: OutcomeMetric }

export type ReadinessCheck =
  | { id: 'history' | 'overlap'; pass: boolean; weeks: number }
  | { id: 'channels'; pass: boolean; channels: readonly string[] }
  | { id: 'continuity'; pass: boolean; gapChannels: readonly string[] }
  | { id: 'outcome'; pass: boolean; weeks: number; total: number; imported: boolean }
  | { id: 'scope'; pass: boolean; state: 'ok' | 'notConfirmed' | 'changed' }
  | { id: 'currency'; pass: boolean; currencies: readonly string[] };

const sameSet = (a: readonly string[], b: readonly string[]): boolean => {
  const bs = new Set(b);
  return new Set(a).size === bs.size && a.every((x) => bs.has(x));
};

/** Check 6: a config exists, and an active version was trained on its scope (as a set) and its metric. */
const scopeState = (r: ReadinessRow, active: ActiveSummary | null): 'ok' | 'notConfirmed' | 'changed' => {
  if (!r.hasConfig) return 'notConfirmed';
  if (active === null) return 'ok';
  return sameSet(active.scopeAccountIds, r.scopeAccountIds) && active.outcomeMetric === r.outcomeMetric ? 'ok' : 'changed';
};

/** The seven checks, in check order. */
export const checksOf = (r: ReadinessRow, active: ActiveSummary | null): readonly ReadinessCheck[] => {
  const state = scopeState(r, active);
  return [
    { id: 'history', pass: r.weeks >= READINESS.historyWeeks, weeks: r.weeks },
    { id: 'overlap', pass: r.overlapWeeks >= READINESS.overlapWeeks, weeks: r.overlapWeeks },
    { id: 'channels', pass: r.channels.length >= READINESS.minChannels, channels: r.channels },
    { id: 'continuity', pass: r.gapChannels.length === 0, gapChannels: r.gapChannels },
    // Coverage over 0 weeks is not measurable, so no window fails.
    {
      id: 'outcome', pass: r.weeks > 0 && r.outcomeWeeks / r.weeks >= READINESS.outcomeCoverage,
      weeks: r.outcomeWeeks, total: r.weeks, imported: r.lastOutcomeDate !== null,
    },
    { id: 'scope', pass: state === 'ok', state },
    { id: 'currency', pass: r.currencies.length === 1, currencies: r.currencies },
  ];
};

/** A confirmed config and every check but scope: a changed scope is what a new download fixes (R19). */
export const downloadAllowed = (r: ReadinessRow, checks: readonly ReadinessCheck[]): boolean =>
  r.hasConfig && checks.every((c) => c.id === 'scope' || c.pass);
